// Status: active
// Durable audience intent identities never expire into permission to create again.
import { z } from 'zod';
import { createAudienceOutputSchema, audienceDraftOutputSchema } from '../marketing/audience-presets.js';
import { audienceDefinitionSchema, nativeAudienceEffectAcknowledgementSchema, nativeAudienceObservationsSchema, nativeAudienceRecordSchema } from '../marketing/native-audience.js';

// This graph is the single authority for routes, transitions and revision reachability.
// Only mirror-claimed states can repeat, acquiring a fresh fence on retry.
export const audienceIntentPaths = {
  'audience:preset': ['admitted', 'segment-pending', 'segment-saved', 'segment-mirror-claimed', 'segment-mirrored', 'complete'],
  'draft:preset': ['admitted', 'segment-pending', 'segment-saved', 'segment-mirror-claimed', 'segment-mirrored', 'campaign-pending', 'campaign-saved', 'campaign-mirror-claimed', 'complete'],
  'draft:existing': ['admitted', 'segment-mirrored', 'campaign-pending', 'campaign-saved', 'campaign-mirror-claimed', 'complete'],
} as const;
export type AudienceIntentPhase = typeof audienceIntentPaths[keyof typeof audienceIntentPaths][number];
const isClaim = (phase: AudienceIntentPhase) => phase === 'segment-mirror-claimed' || phase === 'campaign-mirror-claimed';
function pathFor(action: 'audience' | 'draft', source: 'preset' | 'existing'): readonly AudienceIntentPhase[] {
  if (action === 'audience') return source === 'preset' ? audienceIntentPaths['audience:preset'] : [];
  return audienceIntentPaths[`draft:${source}`];
}
export function isAudienceIntentTransition(prior: { action: 'audience' | 'draft'; creationSource: 'preset' | 'existing'; phase: AudienceIntentPhase }, phase: AudienceIntentPhase) {
  const path = pathFor(prior.action, prior.creationSource);
  const index = path.indexOf(prior.phase);
  return index >= 0 && (path[index + 1] === phase || (isClaim(prior.phase) && prior.phase === phase));
}
const id = z.string().min(1).max(256);
const hash = z.string().regex(/^[a-f0-9]{64}$/);
const identity = {
  schemaVersion: z.literal(2),
  id: z.string().regex(/^marketing#audience-intent#[a-f0-9]{64}$/),
  organizationId: id, clerkOrganizationId: id, actorId: id,
  pocketflowBusinessId: id, targetToken: hash,
  action: z.enum(['audience', 'draft']), creationSource: z.enum(['preset', 'existing']),
  requestId: z.string().uuid(), payloadDigest: hash,
  revision: z.number().int().nonnegative().safe(),
  createdAt: z.string().datetime(), updatedAt: z.string().datetime(),
};
const segment = { pocketflowsSegmentId: id };
const mirrored = { ...segment, segmentId: id, preparedResult: createAudienceOutputSchema.strict().optional() };
const campaign = { ...segment, segmentId: id };
export const audienceIntentRecordSchema = z.discriminatedUnion('phase', [
  z.object({ ...identity, phase: z.literal('admitted') }).strict(),
  z.object({ ...identity, phase: z.literal('segment-pending') }).strict(),
  z.object({ ...identity, ...segment, phase: z.literal('segment-saved') }).strict(),
  z.object({ ...identity, ...segment, phase: z.literal('segment-mirror-claimed') }).strict(),
  z.object({ ...identity, ...mirrored, phase: z.literal('segment-mirrored') }).strict(),
  z.object({ ...identity, ...campaign, phase: z.literal('campaign-pending') }).strict(),
  z.object({ ...identity, ...campaign, phase: z.literal('campaign-saved'), pocketflowsCampaignId: id }).strict(),
  z.object({ ...identity, ...campaign, phase: z.literal('campaign-mirror-claimed'), pocketflowsCampaignId: id }).strict(),
  z.object({ ...identity, ...mirrored, phase: z.literal('complete'), pocketflowsCampaignId: id.optional(), result: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('audience'), value: createAudienceOutputSchema.strict() }).strict(),
    z.object({ kind: z.literal('draft'), value: audienceDraftOutputSchema.strict() }).strict(),
  ]) }).strict(),
]).superRefine((record, ctx) => {
  const path = pathFor(record.action, record.creationSource);
  const minimum = path.indexOf(record.phase);
  const canRepeat = path.slice(0, minimum + 1).some(isClaim);
  if (minimum < 0 || record.revision < minimum || (!canRepeat && record.revision !== minimum)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['revision'], message: 'Intent source, phase and revision are unreachable' });
  }
  if (record.phase === 'segment-mirrored' || record.phase === 'complete') {
    if (record.action === 'audience') {
      if (!record.preparedResult || record.preparedResult.id !== record.segmentId || record.preparedResult.pocketflowsId !== record.pocketflowsSegmentId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Audience mirror checkpoint requires its bound response' });
      }
    } else if (record.preparedResult) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Draft cannot contain an audience response' });
    }
  }
  if (record.phase === 'complete') {
    const value = record.result.value;
    if (record.result.kind !== record.action || (record.result.kind === 'audience'
      ? record.pocketflowsCampaignId !== undefined || JSON.stringify(value) !== JSON.stringify(record.preparedResult)
      : !record.pocketflowsCampaignId || value.pocketflowsId !== record.pocketflowsCampaignId || !('segmentId' in value) || value.segmentId !== record.segmentId)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Intent action and saved IDs/result disagree' });
    }
  }
});
export type AudienceIntentRecord = z.infer<typeof audienceIntentRecordSchema>;
export type AudienceIntentState = AudienceIntentRecord extends infer R ? R extends AudienceIntentRecord
  ? Omit<R, keyof typeof identity> : never : never;

// Preserve identity and validate all known IDs across every legal graph edge.
export function nextAudienceIntentRecord(prior: AudienceIntentRecord, state: AudienceIntentState, nowIso: string): AudienceIntentRecord {
  audienceIntentRecordSchema.parse(prior);
  if (Object.keys(state).some(key => key in identity)) throw new Error('Audience intent identity is immutable');
  if (!isAudienceIntentTransition(prior, state.phase)) throw new Error('Invalid audience intent transition');
  for (const key of ['pocketflowsSegmentId', 'segmentId', 'pocketflowsCampaignId', 'preparedResult'] as const) {
    if (key in prior && JSON.stringify(prior[key as keyof typeof prior]) !== JSON.stringify(state[key as keyof typeof state])) {
      throw new Error('Saved audience intent IDs and response are immutable');
    }
  }
  const base = Object.fromEntries(Object.entries(prior).filter(([key]) => key in identity));
  return audienceIntentRecordSchema.parse({ ...base, ...state, revision: prior.revision + 1, updatedAt: nowIso });
}

export const nativeAudienceIntentPaths = {
  create: ['admitted', 'claimed', 'effect_inflight', 'effect_acknowledged', 'mirror_committed'],
  update: ['admitted', 'claimed', 'effect_inflight', 'effect_acknowledged', 'mirror_committed'],
} as const;
export type NativeAudienceIntentPhase = typeof nativeAudienceIntentPaths[keyof typeof nativeAudienceIntentPaths][number] | 'conflict' | 'rejected' | 'indeterminate';

const nativeProviderStateSchema = z.object({
  name: z.string(),
  description: z.string(),
  filter: z.string().nullable(),
  staticMembershipFingerprint: hash,
  staticMemberCount: z.number().int().nonnegative(),
  providerFingerprint: hash,
}).strict();
export const nativeAudienceIntentPayloadSchema = z.object({
  operation: z.enum(['create', 'update']),
  segmentId: id.optional(),
  pocketflowsSegmentId: id.optional(),
  expectedRevision: hash.optional(),
  name: z.string(),
  description: z.string(),
  definition: audienceDefinitionSchema,
  compiledFilter: z.string().nullable(),
  preserveProviderState: z.boolean(),
  // Omitted for legacy metadata-only updates. This omission is deliberate:
  // provider filter/static membership remain byte-for-byte untouched, even
  // when an old list is larger than the native 1,000-member write bound.
  staticProviderCustomerIds: z.array(id).max(1000).optional(),
  staticMembershipFingerprint: hash,
  staticMemberCount: z.number().int().nonnegative(),
  desiredProviderFingerprint: hash,
  desiredRevision: hash,
  originalProviderState: nativeProviderStateSchema.optional(),
}).strict().superRefine((value, ctx) => {
  const editing = value.operation === 'update';
  const updateIdentity = [value.segmentId, value.pocketflowsSegmentId, value.expectedRevision, value.originalProviderState];
  if ((editing && updateIdentity.some(part => part === undefined)) || (!editing && updateIdentity.some(part => part !== undefined))) {
    ctx.addIssue({ code: 'custom', message: 'Native update intent requires its mirror, provider and original revision' });
  }
  if (!editing && value.definition.kind === 'legacy') ctx.addIssue({ code: 'custom', path: ['definition'], message: 'Legacy definitions cannot be created' });
  if (value.definition.kind === 'legacy' && value.staticProviderCustomerIds !== undefined) {
    ctx.addIssue({ code: 'custom', path: ['staticProviderCustomerIds'], message: 'Legacy membership must be preserved by omission' });
  }
  if ((!editing && value.preserveProviderState) || (value.preserveProviderState && value.staticProviderCustomerIds !== undefined)) {
    ctx.addIssue({ code: 'custom', path: ['preserveProviderState'], message: 'Only metadata-only edits can preserve provider rule and membership by omission' });
  }
  if (value.definition.kind === 'legacy' && !value.preserveProviderState) {
    ctx.addIssue({ code: 'custom', path: ['preserveProviderState'], message: 'Opaque legacy state must be preserved' });
  }
});
export type NativeAudienceIntentPayload = z.infer<typeof nativeAudienceIntentPayloadSchema>;

const nativeIdentity = {
  schemaVersion: z.literal(4),
  id: z.string().regex(/^marketing#audience-intent#[a-f0-9]{64}$/),
  organizationId: id,
  clerkOrganizationId: id,
  actorId: id,
  pocketflowBusinessId: id,
  targetToken: hash,
  action: z.literal('native'),
  creationSource: z.enum(['create', 'update']),
  requestId: z.string().uuid(),
  payloadDigest: hash,
  nativePayload: nativeAudienceIntentPayloadSchema,
  targetCoordinatorId: z.string().regex(/^marketing#audience-target#[a-f0-9]{64}$/),
  // Create commands claim a request-scoped coordinator before the provider
  // target exists. Once the exact provider acknowledgement supplies that
  // identity, targetCoordinatorId moves to the stable target coordinator and
  // this field retains the original claim for atomic alias completion.
  provisionalTargetCoordinatorId: z.string().regex(/^marketing#audience-target#[a-f0-9]{64}$/).optional(),
  targetFence: z.number().int().positive().safe(),
  nativeRevision: z.number().int().nonnegative().safe(),
  revision: z.number().int().nonnegative().safe(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
};
const acknowledgement = { acknowledgement: nativeAudienceEffectAcknowledgementSchema };
const terminal = { observations: nativeAudienceObservationsSchema, reason: z.string().min(1) };
export const nativeAudienceIntentRecordSchema = z.discriminatedUnion('phase', [
  z.object({ ...nativeIdentity, phase: z.literal('admitted') }).strict(),
  z.object({ ...nativeIdentity, phase: z.literal('claimed') }).strict(),
  z.object({ ...nativeIdentity, phase: z.literal('effect_inflight') }).strict(),
  z.object({ ...nativeIdentity, ...acknowledgement, pocketflowsSegmentId: id, phase: z.literal('effect_acknowledged') }).strict(),
  z.object({ ...nativeIdentity, ...acknowledgement, observations: nativeAudienceObservationsSchema, pocketflowsSegmentId: id, segmentId: id, result: nativeAudienceRecordSchema, phase: z.literal('mirror_committed') }).strict(),
  z.object({ ...nativeIdentity, ...terminal, acknowledgement: nativeAudienceEffectAcknowledgementSchema.nullable(), phase: z.literal('conflict') }).strict(),
  z.object({ ...nativeIdentity, ...terminal, acknowledgement: z.null(), phase: z.literal('rejected') }).strict(),
  z.object({ ...nativeIdentity, ...terminal, acknowledgement: z.null(), phase: z.literal('indeterminate') }).strict(),
]).superRefine((record, ctx) => {
  if (record.creationSource !== record.nativePayload.operation) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['creationSource'], message: 'Native intent operation identity is contradictory' });
  }
  const path: readonly string[] = nativeAudienceIntentPaths[record.creationSource];
  const minimum = path.indexOf(record.phase);
  if (minimum >= 0 && record.revision !== minimum) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['revision'], message: 'Native intent phase and revision are unreachable' });
  }
  if ('pocketflowsSegmentId' in record && record.nativePayload.operation === 'update' && record.pocketflowsSegmentId !== record.nativePayload.pocketflowsSegmentId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Native update changed its provider segment identity' });
  }
  if ('acknowledgement' in record && record.acknowledgement && (
    record.acknowledgement.operation !== record.nativePayload.operation
    || ('pocketflowsSegmentId' in record && record.acknowledgement.targetId !== record.pocketflowsSegmentId)
    || (record.nativePayload.operation === 'update' && record.acknowledgement.targetId !== record.nativePayload.pocketflowsSegmentId)
  )) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['acknowledgement'], message: 'Native acknowledgement does not match the admitted operation and target' });
  }
  const acknowledgedCreate = record.creationSource === 'create' && 'acknowledgement' in record && record.acknowledgement !== null;
  if (record.creationSource === 'update' && record.provisionalTargetCoordinatorId !== undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['provisionalTargetCoordinatorId'], message: 'Native updates have a stable target before admission' });
  }
  if (acknowledgedCreate && (!record.provisionalTargetCoordinatorId || record.provisionalTargetCoordinatorId === record.targetCoordinatorId)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['targetCoordinatorId'], message: 'Acknowledged creates require distinct provisional and stable target coordinators' });
  }
  if (!acknowledgedCreate && record.provisionalTargetCoordinatorId !== undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['provisionalTargetCoordinatorId'], message: 'A provisional target alias is retained only after create acknowledgement' });
  }
  if (record.phase === 'mirror_committed' && (record.result.id !== record.segmentId || record.result.revision !== record.nativePayload.desiredRevision)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Native mirror checkpoint does not match its result identity' });
  }
});
export type NativeAudienceIntentRecord = z.infer<typeof nativeAudienceIntentRecordSchema>;
type NativeAudienceIntentStateCore = NativeAudienceIntentRecord extends infer R ? R extends NativeAudienceIntentRecord
  ? Omit<R, keyof typeof nativeIdentity> : never : never;
export type NativeAudienceIntentState = NativeAudienceIntentStateCore extends infer R ? R extends NativeAudienceIntentStateCore
  ? R & { targetCoordinatorId?: string; provisionalTargetCoordinatorId?: string } : never : never;

export function isNativeAudienceIntentTransition(prior: Pick<NativeAudienceIntentRecord, 'creationSource' | 'phase'>, phase: NativeAudienceIntentPhase) {
  if (prior.phase === 'conflict' || prior.phase === 'rejected' || prior.phase === 'indeterminate' || prior.phase === 'mirror_committed') return false;
  if (phase === 'conflict' || phase === 'rejected' || phase === 'indeterminate') return true;
  const path = nativeAudienceIntentPaths[prior.creationSource];
  const index = path.indexOf(prior.phase);
  return index >= 0 && path[index + 1] === phase;
}

export function nextNativeAudienceIntentRecord(prior: NativeAudienceIntentRecord, state: NativeAudienceIntentState, nowIso: string): NativeAudienceIntentRecord {
  const validatedPrior = nativeAudienceIntentRecordSchema.parse(prior);
  const stableCreateBinding = validatedPrior.creationSource === 'create'
    && validatedPrior.phase === 'effect_inflight'
    && state.phase === 'effect_acknowledged'
    && state.targetCoordinatorId !== undefined
    && state.targetCoordinatorId !== validatedPrior.targetCoordinatorId
    && state.provisionalTargetCoordinatorId === validatedPrior.targetCoordinatorId;
  if (Object.keys(state).some(key => key in nativeIdentity && key !== 'targetCoordinatorId' && key !== 'provisionalTargetCoordinatorId')) {
    throw new Error('Native audience intent identity is immutable');
  }
  if ((state.targetCoordinatorId !== undefined || state.provisionalTargetCoordinatorId !== undefined) && !stableCreateBinding) {
    throw new Error('Native audience target identity can bind only from a create acknowledgement');
  }
  if (!isNativeAudienceIntentTransition(validatedPrior, state.phase)) throw new Error('Invalid native audience intent transition');
  for (const key of ['pocketflowsSegmentId', 'segmentId', 'acknowledgement', 'result'] as const) {
    if (key in validatedPrior && key in state && JSON.stringify(validatedPrior[key as keyof typeof validatedPrior]) !== JSON.stringify(state[key as keyof typeof state])) {
      throw new Error('Saved native audience intent IDs and response are immutable');
    }
  }
  const base = Object.fromEntries(Object.entries(validatedPrior).filter(([key]) => key in nativeIdentity));
  return nativeAudienceIntentRecordSchema.parse({ ...base, ...state, revision: validatedPrior.revision + 1, updatedAt: nowIso });
}

const targetBase = {
  schemaVersion: z.literal(1), id: z.string().regex(/^marketing#audience-target#[a-f0-9]{64}$/),
  organizationId: id, clerkOrganizationId: id, pocketflowBusinessId: id, targetToken: hash,
  pocketflowsSegmentId: id.optional(), segmentId: id.optional(), activeIntentId: z.string().regex(/^marketing#audience-intent#[a-f0-9]{64}$/),
  fence: z.number().int().positive().safe(), nativeRevision: z.number().int().nonnegative().safe(),
  baseRevision: hash.nullable(), currentRevision: hash.nullable(), stateVersion: z.number().int().nonnegative().safe(),
  createdAt: z.string().datetime(), updatedAt: z.string().datetime(),
};
export const nativeAudienceTargetCoordinatorSchema = z.discriminatedUnion('phase', [
  z.object({ ...targetBase, phase: z.enum(['claimed', 'effect_inflight']) }).strict(),
  z.object({ ...targetBase, ...acknowledgement, pocketflowsSegmentId: id, phase: z.literal('effect_acknowledged') }).strict(),
  z.object({ ...targetBase, ...acknowledgement, observations: nativeAudienceObservationsSchema, pocketflowsSegmentId: id, segmentId: id, record: nativeAudienceRecordSchema, phase: z.literal('mirror_committed') }).strict(),
  z.object({ ...targetBase, ...terminal, acknowledgement: nativeAudienceEffectAcknowledgementSchema.nullable(), phase: z.literal('conflict') }).strict(),
  z.object({ ...targetBase, ...terminal, acknowledgement: z.null(), phase: z.enum(['rejected', 'indeterminate']) }).strict(),
]).superRefine((record, ctx) => {
  if ('acknowledgement' in record && record.acknowledgement && record.acknowledgement.targetId !== record.pocketflowsSegmentId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Target acknowledgement changed provider identity' });
  }
  if (record.phase === 'mirror_committed' && (record.record.id !== record.segmentId || record.record.revision !== record.currentRevision || record.record.targetToken !== record.targetToken)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Committed target and canonical record disagree' });
  }
});
export type NativeAudienceTargetCoordinator = z.infer<typeof nativeAudienceTargetCoordinatorSchema>;
export type NativeAudienceTargetMutation = { prior: NativeAudienceTargetCoordinator | null; next: NativeAudienceTargetCoordinator };
